import * as Models from "../Models";
import _ from "lodash";
import * as searchHelpers from "./search";
import * as Const from "../Constants";
import { v4 as uuid } from "uuid";
import * as moment from "moment";

export const getTableColumns: Function = (
  state: Models.SearchState
): Models.SearchResultColumn[] => _.sortBy(state.tableColumns, "order");

export const getExportColumns = (state): Models.ExportColumn[] =>
  _.sortBy(state.exportColumns, "order");

export const getTableColumnsByResultView: Function = (
  state: Models.SearchState,
  order: boolean
): Models.SearchResultColumn[] => {
  if (!state.searchIndex || !state.tableColumns) {
    return [];
  }
  const resultGroup: Models.SearchSettingGroupType =
    searchHelpers.searchIndexToGroupType(state.searchIndex);
  let columns: Models.SearchResultColumn[] = getTableColumns(state);

  const classAttributeColumns: Models.SearchResultColumn[] = _.filter(
    columns,
    (c: Models.SearchResultColumn) => c.classAttribute === true
  );

  _.forEach(classAttributeColumns, (c: Models.SearchResultColumn) => {
    var uomCol: Models.SearchResultColumn = {
      active: c.active,
      classAttribute: c.classAttribute,
      columnFieldInResultViews: c.columnFieldInResultViews,
      displayName: c.displayName + " UoM",
      filterable: c.filterable,
      isColumn: c.isColumn,
      order: c.order,
      searchField: c.searchField + "Uom",
      filterGroupOrder: c.filterGroupOrder,
      searchResultOrders: c.searchResultOrders,
    };

    if (
      !columns.some(
        (column) =>
          column["displayName"]?.includes(c.displayName) &&
          column["displayName"]?.endsWith("UoM")
      )
    ) {
      columns.push(uomCol);
    }
  });

  return _.filter(
    columns,
    (c: Models.SearchResultColumn) =>
      c.columnFieldInResultViews?.indexOf(resultGroup) > -1
  );
};

export const setColumnDefaultValues: (
  state,
  searchIndex: string
) => Models.SearchResultColumn[] = (state, searchIndex) => {
  let columns: Models.SearchResultColumn[] = [...state.tableColumns];
  _.forEach(columns, (c: Models.SearchResultColumn) => {
    c.active = getColumnDefaultValue(searchIndex, c);
  });

  return _.filter(columns, (c) => !(c.classAttribute && !c.active));
};

export const getColumnDefaultValue: (
  searchIndex: string,
  col: Models.SearchResultColumn
) => boolean = (searchIndex, col) => {
  const defaultActive: boolean = false;
  switch (searchIndex) {
    case Const.SEARCH_RESULT_BUILDS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Builds
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Builds
        ).defaultColumn;
      }
      break;
    }
    case Const.SEARCH_RESULT_CUSTOMERS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Customers
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Customers
        ).defaultColumn;
      }
      break;
    }
    case Const.SEARCH_RESULT_LOCATIONS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Locations
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Locations
        ).defaultColumn;
      }
      break;
    }
    case Const.SEARCH_RESULT_PRODUCTS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Products
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Products
        ).defaultColumn;
      }
      break;
    }
    case Const.SEARCH_RESULT_DOCUMENTS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Documents
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Documents
        ).defaultColumn;
      }
      break;
    }
  }

  return defaultActive;
};

export const getColumnSortValue: Function = (
  searchIndex: string,
  col: Models.SearchResultColumn
): number => {
  const defaultOrder: number = 99;
  switch (searchIndex) {
    case Const.SEARCH_RESULT_BUILDS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Builds
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Builds
        ).order;
      }
      break;
    }
    case Const.SEARCH_RESULT_CUSTOMERS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Customers
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Customers
        ).order;
      }
      break;
    }
    case Const.SEARCH_RESULT_LOCATIONS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Locations
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Locations
        ).order;
      }
      break;
    }
    case Const.SEARCH_RESULT_PRODUCTS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Products
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Products
        ).order;
      }
      break;
    }
    case Const.SEARCH_RESULT_DOCUMENTS: {
      if (
        col.searchResultOrders &&
        col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Documents
        ) != null
      ) {
        return col.searchResultOrders.find(
          (c) => c.view === Models.SearchResultView.Documents
        ).order;
      }
      break;
    }
  }
  return defaultOrder;
};

export const getNonEmptyFilters = (
  filters: Models.SearchFilters
): Models.SearchFilters => {
  const nonEmptyFilters: any = {};
  _.forOwn(filters, (filterValues, key: string) => {
    const nonEmptyFilterValues: Models.SearchFilterValue[] = _.filter(
      filterValues,
      (f: Models.SearchFilterValue) => f.value.trim() !== ""
    );

    if (nonEmptyFilterValues.length > 0) {
      nonEmptyFilters[key] = nonEmptyFilterValues;
    }
    const nonEmptyComparisonFilterValues: Models.SearchFilterValue[] = _.filter(
      filterValues,
      (f: Models.SearchFilterValue) =>
        f.compareTo !== "" || f.compareFrom !== ""
    );
    if (nonEmptyComparisonFilterValues.length > 0) {
      nonEmptyFilters[key] = nonEmptyComparisonFilterValues;
    }
  });
  return nonEmptyFilters;
};

export const getPayload = (state): Models.AdvancedSearchApiPayload => {
  const filters = state.filters;
  const options = state.options;
  const columns = getTableColumnsByResultView(state, false);
  const searchIndex = state.searchIndex;
  const removedColumns = state.removedColumns;
  const exportColumns: Models.ExportColumn[] = getExportColumns(state);

  // Pagination can be implemented by making same query but just change the value of skip and top parameters.
  // First call with skip = 0 and take = 1000, second with skip = 1000 and take = 1000, third with skip = 2000 and take = 1000 and so on.
  // Page size could be other than 1000 as well, like 10000. Best page size can be decided when testing different queries.
  // If there are less results than what was asked, pagination can be interrupted and no more additional requests would return more results.
  const skip: number = 0; // Skip this number of results from the start
  const top: number = 100000; // Maximum number of results to be returned, this was default in backend but this is definetly too big for some queries

  const advancedSearchPayload: Models.AdvancedSearchApiPayload = {
    searchIndex,
    searchRules: [],
    filters: [],
    showLoading: true,
    columns,
    removedColumns,
    exportColumns,
    skip,
    top,
  };

  const nonEmptyFilters: Models.SearchFilters = getNonEmptyFilters(filters);
  _.forOwn(
    nonEmptyFilters,
    (filterValues: Models.SearchFilterValue[], filterKey: string) => {
      const option: Models.SearchSettingField = options[filterKey];
      addSearchParameter(advancedSearchPayload, option, filterValues);
    }
  );

  return advancedSearchPayload;
};

export const addSearchParameter: (
  payload: Models.AdvancedSearchApiPayload,
  option: Models.SearchSettingField,
  filterValues: Models.SearchFilterValue[]
) => void = (payload, option, filterValues) => {
  if (
    option.elementType === Models.SearchSettingElementType.SelectFromOptions
  ) {
    handleClassAttributeField(payload, option, filterValues);
    return;
  }

  if (
    option.fieldType === Models.SearchSettingFieldType.Rule ||
    option.fieldType === Models.SearchSettingFieldType.RuleAndFilter
  ) {
    handleRule(payload, option, filterValues);
  }

  if (
    option.fieldType === Models.SearchSettingFieldType.Filter ||
    option.fieldType === Models.SearchSettingFieldType.RuleAndFilter
  ) {
    handleFilter(payload, option, filterValues);
  }
};

export const handleRule: (
  payload: Models.AdvancedSearchApiPayload,
  option: Models.SearchSettingField,
  filterValues: Models.SearchFilterValue[]
) => void = (payload, option, filterValues) => {
  _.forEach(filterValues, (field: Models.SearchFilterValue) => {
    const rule: Models.SearchRule = {
      field: option,
      fieldValues: [field.value],
      operator: filterValues.length > 1 ? "or" : "and",
    };

    if (
      option.elementType ===
        Models.SearchSettingElementType.TextWithOperators ||
      option.elementType === Models.SearchSettingElementType.SelectWithOperators
    ) {
      rule.operator = field.operator ? field.operator : "or";
      rule.comparisonOperator = field.comparisonOperator
        ? field.comparisonOperator
        : "contains";
    }

    payload.searchRules.push(rule);
  });
};

export const handleFilter: (
  payload: Models.AdvancedSearchApiPayload,
  option: Models.SearchSettingField,
  filterValues: Models.SearchFilterValue[]
) => void = (payload, option, filterValues) => {
  _.forEach(filterValues, (field: Models.SearchFilterValue) => {
    const filter: Models.SearchFilter = {
      operator: field.operator ? field.operator : "or",
      field: option,
      fieldValue: field.value,
      comparisonOperator: field.comparisonOperator
        ? field.comparisonOperator
        : "eq",
    };

    if (!!field.compareFrom) {
      filter.compareFrom = field.compareFrom;
    }
    if (!!field.compareTo) {
      filter.compareTo = field.compareTo;
    }

    payload.filters.push(filter);
  });
};

export const handleClassAttributeField: (
  payload: Models.AdvancedSearchApiPayload,
  option: Models.SearchSettingField,
  filterValues: Models.SearchFilterValue[]
) => void = (payload, option, filterValues) => {
  _.forEach(filterValues, (field: Models.SearchFilterValue) => {
    const comparisonOperator: string = field.comparisonOperator
      ? field.comparisonOperator
      : "eq";
    const operator: string = field.operator ? field.operator : "or";

    if (comparisonOperator === "between") {
      const fromFilter: Models.SearchFilter = {
        operator: "and",
        field: option,
        fieldValue: field.value,
        comparisonOperator: "ge",
        compareFrom: field.compareFrom.trim(),
      };

      const toFilter: Models.SearchFilter = {
        operator,
        field: option,
        fieldValue: field.value,
        comparisonOperator: "le",
        compareTo: field.compareTo.trim(),
      };

      payload.filters.push(fromFilter);
      payload.filters.push(toFilter);
    } else {
      if (comparisonOperator.indexOf("contain") > -1) {
        const rule: Models.SearchRule = {
          operator,
          field: option,
          comparisonOperator,
          fieldValues: [field.compareTo],
          classAttributeSearchIndexField: field.value,
        };

        payload.searchRules.push(rule);
      } else {
        const filter: Models.SearchFilter = {
          operator,
          field: option,
          fieldValue: field.value,
          compareTo: field.compareTo.trim(),
          comparisonOperator,
        };
        payload.filters.push(filter);
      }
    }
  });
};

export function combineResults(
  results: Models.DetailCollection,
  partResults: Models.DetailCollection
): Models.DetailCollection {
  if (results === null) {
    results = {
      type: partResults.type,
      builds: partResults.builds ?? [],
      customers: partResults.customers ?? [],
      locations: partResults.locations ?? [],
      products: partResults.products ?? [],
      sites: partResults.sites ?? [],
      documents: partResults.documents ?? [],
    };
  } else {
    results.builds = results.builds.concat(partResults.builds);
    results.customers = results.customers.concat(partResults.customers);
    results.locations = results.locations.concat(partResults.locations);
    results.products = results.products.concat(partResults.products);
    results.sites = results.sites.concat(partResults.sites);
    results.documents = results.documents.concat(partResults.documents);
  }
  return results;
}

export const getExportPayload: (
  state: Models.SearchState
) => Models.AdvancedSearchApiPayload = (state) => {
  const advancedSearchPayload: Models.AdvancedSearchApiPayload =
    getPayload(state);
  advancedSearchPayload.groupBy = state.groupBy;
  advancedSearchPayload.exportColumns = _.sortBy(state.exportColumns, "order");
  handleReactTableRules(state, advancedSearchPayload);
  return advancedSearchPayload;
};

const handleReactTableRules: (
  state: Models.SearchState,
  advancedSearchPayload: Models.AdvancedSearchApiPayload
) => void = (state, advancedSearchPayload) => {
  const optionValues: Models.SearchSettingField[] = _.values(
    // Selectors.getAdvancedSearchOptions(state)
    state.options
  );
  const tableFilters: Models.ReactTableFilter[] =
    // Selectors.getTableFilters(state);
    state.tableFilters;
  // const sort: any = Selectors.getTableSort(state);
  const sort: any = state.tableSort;

  _.forEach(tableFilters, (f: Models.SearchFilterValue) => {
    const option: Models.SearchSettingField = _.find(
      optionValues,
      (o) => o.searchIndexField === f.label
    );
    const rule: Models.SearchRule = {
      field: option,
      comparisonOperator: "contains",
      fieldValues: [f.value],
      operator: "and",
      classAttributeSearchIndexField: "",
    };
    advancedSearchPayload.searchRules.push(rule);
  });

  advancedSearchPayload.sort = sort;
};

export const saveQueryPayload = (
  state: Models.SearchState,
  displayName: string
) => {
  if (!displayName) {
    return;
  }

  // const state = get();
  // const { getRecentSearchQueries } = state;
  const filters: Models.SearchFilters = state.filters;
  const columns: Models.SearchResultColumn[] = getTableColumnsByResultView(
    state,
    false
  );
  const activeColumns: Models.SearchResultColumn[] = _.filter(
    columns,
    (c) => c.active
  );
  const nonEmptyFilters: Models.SearchFilters = getNonEmptyFilters(filters);

  const payload: Models.UserSearch = {
    id: uuid(),
    searchIndex: state.searchIndex,
    displayName,
    filters: nonEmptyFilters,
    date: moment.utc().format(),
    activeColumns,
  };
  return payload;
};

export const shareQueryPayload = (state: Models.SearchState) => {
  const filters = state.filters;
  const columns: Models.SearchResultColumn[] = getTableColumnsByResultView(
    state,
    false
  );
  const activeColumns: Models.SearchResultColumn[] = _.filter(
    columns,
    (c) => c.active
  );
  const nonEmptyFilters: Models.SearchFilters = getNonEmptyFilters(filters);

  const payload: Models.UserSearch = {
    id: uuid(),
    searchIndex: state.searchIndex,
    displayName: uuid(),
    filters: nonEmptyFilters,
    date: moment.utc().format(),
    activeColumns,
  };
  return payload;
};
